import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axiosInstance from '../services/axiosInstance';
import { useAuth } from './AuthContext';
import { getTenant } from '../utils/tenantUtils';
import {
  DEFAULT_NAVIGATION_PREFERENCES,
  resolveHomePath,
} from '../utils/homePage';

const NavigationPreferencesContext = createContext(null);

export const NavigationPreferencesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const tenant = getTenant();
  const [preferences, setPreferences] = useState(DEFAULT_NAVIGATION_PREFERENCES);
  const [loading, setLoading] = useState(false);

  const refreshPreferences = useCallback(async () => {
    if (!isAuthenticated) {
      setPreferences(DEFAULT_NAVIGATION_PREFERENCES);
      return;
    }
    setLoading(true);
    try {
      const response = await axiosInstance.get('/user/profile/');
      setPreferences(
        response.data.navigation_preferences || DEFAULT_NAVIGATION_PREFERENCES
      );
    } catch (error) {
      console.error('Error loading navigation preferences:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    refreshPreferences();
  }, [refreshPreferences, tenant]);

  useEffect(() => {
    const onUpdated = () => refreshPreferences();
    window.addEventListener('homePreferencesUpdated', onUpdated);
    return () => window.removeEventListener('homePreferencesUpdated', onUpdated);
  }, [refreshPreferences]);

  const getHomePath = useCallback(
    (platform = 'desktop') => resolveHomePath(tenant, preferences, platform),
    [tenant, preferences]
  );

  const value = useMemo(
    () => ({
      preferences,
      loading,
      refreshPreferences,
      getHomePath,
      desktopHomePath: resolveHomePath(tenant, preferences, 'desktop'),
      mobileHomePath: resolveHomePath(tenant, preferences, 'mobile'),
    }),
    [preferences, loading, refreshPreferences, getHomePath, tenant]
  );

  return (
    <NavigationPreferencesContext.Provider value={value}>
      {children}
    </NavigationPreferencesContext.Provider>
  );
};

export const useNavigationPreferences = () => {
  const context = useContext(NavigationPreferencesContext);
  if (!context) {
    throw new Error('useNavigationPreferences must be used within NavigationPreferencesProvider');
  }
  return context;
};
